<template>
  <div class="space-y-5">
    <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
      <p class="text-sm text-slate-500 dark:text-slate-400">
        Upload and manage your important documents.
      </p>
      <button
        type="button"
        class="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-lg font-semibold text-white bg-primary hover:bg-secondary shadow-md transition w-full sm:w-auto"
        @click="showUploadModal = true"
      >
        <FilePlusCorner class="h-5 w-5" />
        Upload Document
      </button>
    </div>

    <TableControls
      v-model:search="searchQuery"
      v-model:status="statusFilter"
      placeholder="Search by title..."
      :options="statusOptions"
    />

    <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      <div v-for="n in 6" :key="n" class="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-5 animate-pulse">
        <div class="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-3"></div>
        <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4"></div>
        <div class="flex gap-2 pt-4 border-t border-gray-100 dark:border-gray-700">
          <div class="h-9 w-9 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
          <div class="h-9 w-9 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
        </div>
      </div>
    </div>

    <div v-else-if="error" class="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-xl border border-red-200 dark:border-red-800">
      {{ error }}
    </div>

    <div v-else-if="filteredDocuments.length === 0" class="text-center py-16 text-slate-500 dark:text-slate-400">
      <FileText class="h-12 w-12 mx-auto mb-3 text-slate-300 dark:text-slate-600" />
      <p class="text-base font-medium">No documents found</p>
      <p class="text-sm mt-1">{{ searchQuery.trim() ? 'Try a different search term.' : 'Upload your first document to get started.' }}</p>
    </div>

    <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
      <div v-for="doc in filteredDocuments" :key="doc.id" class="relative bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-5 flex flex-col">
        <div class="flex items-start justify-between gap-2 mb-2">
          <h3 class="font-semibold text-base text-gray-800 dark:text-gray-200 truncate">{{ doc.title }}</h3>
          <span class="shrink-0 px-2.5 py-0.5 text-xs font-semibold rounded-full"
            :class="doc.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'">
            {{ doc.status }}
          </span>
        </div>
        <p class="text-sm text-gray-500 dark:text-gray-400 mb-4 flex-1">
          Uploaded {{ new Date(doc.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) }}
        </p>
        <div class="flex items-center gap-2 pt-4 border-t border-gray-100 dark:border-gray-700">
          <button type="button" @click="viewDocument(doc)"
            class="p-2 rounded-lg text-blue-600 hover:bg-blue-600/10 dark:hover:bg-primary-dark/10 transition-all" title="View Document">
            <Eye class="h-5 w-5" />
          </button>
          <button type="button" @click="downloadDocument(doc)"
            class="p-2 rounded-lg text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all" title="Download Document">
            <DownloadIcon class="h-5 w-5" />
          </button>
          <button type="button" @click="openEditModal(doc)"
            class="p-2 rounded-lg text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-all" title="Edit Document">
            <Pencil class="h-5 w-5" />
          </button>
          <button type="button" @click="handleDelete(doc.id)"
            class="p-2 rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all" title="Delete Document">
            <Trash class="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>

    <Modal v-model="showDeleteModal" title="Delete Document">
      <p class="text-sm text-slate-600 dark:text-slate-300">
        Are you sure you want to permanently delete
        <span class="font-semibold text-slate-700 dark:text-slate-200">"{{ pendingDeleteTitle }}"</span>?
        This action cannot be undone.
      </p>
      <template #footer>
        <button type="button"
          class="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
          @click="showDeleteModal = false">
          Cancel
        </button>
        <button type="button"
          class="ml-2 px-4 py-2 rounded-lg text-sm font-semibold text-white bg-red-600 hover:bg-red-700 shadow-sm transition disabled:opacity-60"
          :disabled="deleteLoading" @click="confirmDelete">
          {{ deleteLoading ? 'Deleting...' : 'Yes, delete permanently' }}
        </button>
      </template>
    </Modal>

    <Modal v-model="showUploadModal" title="Upload Document">
      <div class="space-y-4">
        <div>
          <label class="label">Document Title <span class="text-danger">*</span></label>
          <input
            v-model="uploadTitle"
            type="text"
            class="input"
            placeholder="e.g. CPR Certification"
          />
        </div>
        <div
          class="flex items-center gap-4 w-full rounded-2xl border-2 border-dashed p-6 cursor-pointer select-none"
          @click="triggerFileInput"
          @dragover.prevent="dragOver = true"
          @dragleave.prevent="dragOver = false"
          @drop.prevent="onDrop"
          :class="dragOver ? 'bg-primary/5 rounded-xl p-2 -m-2' : ''"
        >
          <div
            class="h-11 w-11 rounded-xl flex items-center justify-center
                   bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200"
          >
            <Upload class="h-5 w-5" />
          </div>

          <div class="min-w-0 flex-1 overflow-hidden">
            <template v-if="selectedFileName">
              <p class="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate" :title="selectedFileName">
                {{ selectedFileName }}
              </p>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                Click or drop to change file
              </p>
            </template>
            <template v-else>
              <p class="text-sm font-semibold text-gray-800 dark:text-gray-100">
                Drag &amp; drop your file here
              </p>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                or click to browse
              </p>
            </template>
          </div>
          <input
            ref="fileInputRef"
            type="file"
            class="hidden"
            @change="handleFileChange"
          />
        </div>
        <p v-if="uploadError" class="error-message">{{ uploadError }}</p>
      </div>
      <template #footer>
        <button
          type="button"
          class="px-4 py-2 border rounded-lg dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          @click="showUploadModal = false"
        >
          Cancel
        </button>
        <button
          type="button"
          class="ml-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-secondary text-sm font-semibold disabled:opacity-50"
          :disabled="uploading"
          @click="uploadDocument"
        >
          {{ uploading ? 'Uploading...' : 'Upload' }}
        </button>
      </template>
    </Modal>

    <Modal v-model="showEditModal" title="Edit Document">
      <div class="space-y-4">
        <div>
          <label class="label">Document Title <span class="text-danger">*</span></label>
          <input v-model="editTitle" type="text" class="input" placeholder="e.g. CPR Certification" />
        </div>
        <div>
          <label class="label">File (leave empty to keep current)</label>
          <div
            class="flex items-center gap-4 w-full rounded-2xl border-2 border-dashed p-6 cursor-pointer select-none"
            @click="editFileInputRef?.click()"
            @dragover.prevent="editDragOver = true"
            @dragleave.prevent="editDragOver = false"
            @drop.prevent="onEditDrop"
            :class="editDragOver ? 'bg-primary/5' : ''"
          >
            <div class="h-11 w-11 rounded-xl flex items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-200">
              <Upload class="h-5 w-5" />
            </div>
            <div class="min-w-0 flex-1 overflow-hidden">
              <template v-if="editFile">
                <p class="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate" :title="editFile.name">{{ editFile.name }}</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">Click or drop to change file</p>
              </template>
              <template v-else>
                <p class="text-sm font-semibold text-gray-800 dark:text-gray-100">Keep current file</p>
                <p class="text-xs text-gray-500 dark:text-gray-400">or click to browse for a new file</p>
              </template>
            </div>
            <input ref="editFileInputRef" type="file" class="hidden" @change="onEditFileChange" />
          </div>
        </div>
        <p v-if="editError" class="error-message">{{ editError }}</p>
      </div>
      <template #footer>
        <button type="button"
          class="px-4 py-2 border rounded-lg dark:border-gray-600 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          @click="showEditModal = false">Cancel</button>
        <button type="button"
          class="ml-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-secondary text-sm font-semibold disabled:opacity-50"
          :disabled="editLoading" @click="confirmEdit">
          {{ editLoading ? 'Saving...' : 'Save' }}
        </button>
      </template>
    </Modal>

    <!-- View Document Modal -->
    <DocumentDetailsModal
      :is-open="showViewModal"
      :document="selectedDocument"
      @close="showViewModal = false; selectedDocument = null"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { FilePlusCorner, Eye, Trash, Upload, FileText, Download as DownloadIcon, Pencil } from 'lucide-vue-next'
import { employeeDocumentService, type EmployeeDocument } from '@/services/employeeDocumentService'
import Modal from '@/components/layout/Modal.vue'
import DocumentDetailsModal from '@/components/documents/DocumentDetailsModal.vue'
import TableControls from '@/components/layout/TableControls.vue'
import { useToast } from 'vue-toastification'

const toast = useToast()
const toastTimeout = 5000

const documents = ref<EmployeeDocument[]>([])
const loading = ref(false)
const error = ref('')
const showDeleteModal = ref(false)
const deleteLoading = ref(false)
const pendingDeleteId = ref<number | null>(null)
const pendingDeleteTitle = ref('')

const searchQuery = ref('')
const statusFilter = ref('')
const statusOptions = [
  { value: '', label: 'All' },
]

const showUploadModal = ref(false)
const uploadTitle = ref('')
const uploadFile = ref<File | null>(null)
const uploadError = ref('')
const uploading = ref(false)
const dragOver = ref(false)
const fileInputRef = ref<HTMLInputElement | null>(null)

const showViewModal = ref(false)
const selectedDocument = ref<EmployeeDocument | null>(null)
const showEditModal = ref(false)
const editTitle = ref('')
const editFile = ref<File | null>(null)
const editError = ref('')
const editLoading = ref(false)
const editDragOver = ref(false)
const editFileInputRef = ref<HTMLInputElement | null>(null)
const editingDocId = ref<number | null>(null)

const selectedFileName = computed(() => uploadFile.value?.name || '')

const filteredDocuments = computed(() => {
  if (!searchQuery.value.trim()) return documents.value
  const q = searchQuery.value.toLowerCase()
  return documents.value.filter(d => (d.title || '').toLowerCase().includes(q))
})

const fetchDocuments = async () => {
  loading.value = true
  error.value = ''
  try {
    documents.value = await employeeDocumentService.fetchAll()
  } catch {
    error.value = 'Failed to load your documents.'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchDocuments()
})

const triggerFileInput = () => {
  fileInputRef.value?.click()
}

const onDrop = (e: DragEvent) => {
  dragOver.value = false
  const file = e.dataTransfer?.files?.[0]
  if (file) uploadFile.value = file
}

const openEditModal = (doc: EmployeeDocument) => {
  editingDocId.value = doc.id
  editTitle.value = doc.title
  editFile.value = null
  editError.value = ''
  showEditModal.value = true
}

const onEditDrop = (e: DragEvent) => {
  editDragOver.value = false
  const file = e.dataTransfer?.files?.[0]
  if (file) editFile.value = file
}

const onEditFileChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  editFile.value = target.files?.[0] || null
}

const confirmEdit = async () => {
  if (!editingDocId.value) return
  if (!editTitle.value.trim()) {
    editError.value = 'Title is required.'
    return
  }

  const formData = new FormData()
  formData.append('title', editTitle.value.trim())
  if (editFile.value) {
    formData.append('file', editFile.value)
  }

  editLoading.value = true
  editError.value = ''
  try {
    await employeeDocumentService.update(editingDocId.value, formData)
    toast.success('Document updated successfully', { timeout: toastTimeout })
    showEditModal.value = false
    fetchDocuments()
  } catch (err: unknown) {
    const axiosErr = err as { response?: { data?: { message?: string } } }
    editError.value = axiosErr.response?.data?.message || 'Failed to update document.'
  } finally {
    editLoading.value = false
  }
}

const handleFileChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  uploadFile.value = target.files?.[0] || null
}

const uploadDocument = async () => {
  uploadError.value = ''
  if (!uploadTitle.value.trim()) {
    uploadError.value = 'Title is required.'
    return
  }
  if (!uploadFile.value) {
    uploadError.value = 'Please select a file.'
    return
  }

  const formData = new FormData()
  formData.append('title', uploadTitle.value.trim())
  formData.append('file', uploadFile.value)

  uploading.value = true
  try {
    await employeeDocumentService.create(formData)
    toast.success('Document uploaded successfully', { timeout: toastTimeout })
    showUploadModal.value = false
    uploadTitle.value = ''
    uploadFile.value = null
    fetchDocuments()
  } catch (err: unknown) {
    const axiosErr = err as { response?: { data?: { message?: string } } }
    uploadError.value = axiosErr.response?.data?.message || 'Failed to upload document.'
  } finally {
    uploading.value = false
  }
}

const viewDocument = (doc: EmployeeDocument) => {
  selectedDocument.value = doc
  showViewModal.value = true
}

const downloadDocument = async (doc: EmployeeDocument) => {
  const loadingToast = toast.info('Preparing document for download...', { timeout: false })
  try {
    const token = localStorage.getItem('token')
    const url = employeeDocumentService.getDownloadUrl(doc.id)
    const response = await fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!response.ok) throw new Error('Invalid File')
    const blob = await response.blob()
    const blobUrl = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = blobUrl
    a.download = doc.title || 'download'
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(blobUrl)
    document.body.removeChild(a)
    toast.dismiss(loadingToast)
    toast.success('Document downloaded successfully', { timeout: toastTimeout })
  } catch {
    toast.dismiss(loadingToast)
    toast.error('Failed to download document', { timeout: toastTimeout })
  }
}

const handleDelete = (id: number) => {
  const doc = documents.value.find(d => d.id === id)
  pendingDeleteId.value = id
  pendingDeleteTitle.value = doc?.title || 'this document'
  showDeleteModal.value = true
}

const confirmDelete = async () => {
  if (pendingDeleteId.value === null) return
  deleteLoading.value = true
  try {
    await employeeDocumentService.delete(pendingDeleteId.value)
    toast.success('Document deleted', { timeout: toastTimeout })
    showDeleteModal.value = false
    pendingDeleteId.value = null
    fetchDocuments()
  } catch {
    toast.error('Failed to delete document', { timeout: toastTimeout })
  } finally {
    deleteLoading.value = false
  }
}
</script>
